
import { useState, useEffect } from "react";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useSubscription } from "../useSubscription";
import { useBillingPortal } from "@/hooks/subscription/useBillingPortal";
import { SubscriptionPlanType } from "../utils";
import { toast } from "sonner";

export const useSubscriptionDialog = (userId: string, userEmail?: string) => {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanType>('essentials');
  const { data: subscriptionStatus, refetch: refetchSubscription } = useSubscriptionStatus(userId);
  
  // Initialize isAnnual based on subscription metadata
  const [isAnnual, setIsAnnual] = useState(false);
  
  const { 
    isLoading, 
    planConfigs, 
    isPlansLoading, 
    handleCreateCheckoutSession 
  } = useSubscription(userId, userEmail);
  
  const {
    handleManageSubscription,
    handleDowngradeSubscription,
    isLoading: isPortalLoading,
    isDowngradeLoading
  } = useBillingPortal(userId);
  
  const [updateComplete, setUpdateComplete] = useState(false);
  const [lastSelectedPlan, setLastSelectedPlan] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [downgradeSuccess, setDowngradeSuccess] = useState(false);
  const [downgradeMessage, setDowngradeMessage] = useState<string | null>(null);

  useEffect(() => {
    if (subscriptionStatus?.plan) {
      const planType = subscriptionStatus.plan as string;
      if (planType === 'essentials' || planType === 'growth' || planType === 'scale') {
        setSelectedPlan(planType as SubscriptionPlanType);
      } else {
        setSelectedPlan('essentials');
      }
    } else {
      setSelectedPlan('essentials');
    }
  }, [subscriptionStatus?.plan]);

  useEffect(() => {
    if (subscriptionStatus) {
      const isSubscriptionAnnual = 
        subscriptionStatus.metadata?.isAnnual === true || 
        subscriptionStatus.metadata?.billing_cycle === 'annual';
      
      console.log("📊 Subscription annual status:", isSubscriptionAnnual);
      setIsAnnual(!!isSubscriptionAnnual);
    }
  }, [subscriptionStatus]);

  const handleConfirmPlan = async () => {
    try {
      console.log("🔄 Starting plan confirmation process");
      console.log(`🔄 Selected plan: ${selectedPlan}${isAnnual ? ' (annual)' : ' (monthly)'}`);
      console.log(`🔄 Last selected plan: ${lastSelectedPlan || 'none'}`);
      
      if (selectedPlan !== lastSelectedPlan || lastSelectedPlan === null) {
        console.log("🔄 Plan changed or first selection - creating checkout session");
        setLastSelectedPlan(selectedPlan);
        
        setUpdateComplete(true);
        const currentPlan = subscriptionStatus?.plan || 'trial';
        console.log(`🔄 Current plan: ${currentPlan}`);
        
        console.log("🔄 Creating checkout session...");
        const result = await handleCreateCheckoutSession(
          selectedPlan,
          currentPlan,
          isAnnual
        );
        
        if (result.success && result.url) {
          console.log("✅ Checkout session created successfully, redirecting to:", result.url);
          window.location.href = result.url;
          return;
        } else if (result.success && result.isProratedUpgrade) {
          console.log("✅ Prorated upgrade processed successfully");
          return;
        } else {
          console.error("❌ Failed to create checkout session:", result);
          setUpdateComplete(false);
          toast.error("Failed to create checkout session");
        }
      } else {
        console.log("ℹ️ Plan already selected, not making another update:", selectedPlan);
        setTimeout(() => {
          setUpdateComplete(false);
        }, 500);
      }
    } catch (error) {
      console.error('❌ Error confirming plan:', error);
      setUpdateComplete(false);
      toast.error("An error occurred while updating your subscription");
    }
  };

  const handleOpenBillingPortal = () => {
    console.log("🔄 Opening billing portal");
    setIsRedirecting(true);
    handleManageSubscription()
      .then((result) => {
        console.log("🔄 Billing portal result:", result);
        if (!result.success) {
          console.error("❌ Failed to open billing portal");
          toast.error("Failed to open billing portal");
          setIsRedirecting(false);
        }
        setTimeout(() => {
          if (document.hasFocus()) {
            setIsRedirecting(false);
          }
        }, 5000);
      })
      .catch((error) => {
        console.error("❌ Error opening billing portal:", error);
        toast.error("Failed to open billing portal");
        setIsRedirecting(false);
      });
  };

  const handleDowngrade = async (currentPlan: string, newPlan: string) => {
    console.log(`🔄 Initiating downgrade from ${currentPlan} to ${newPlan} plan`);
    setIsRedirecting(true);
    try {
      console.log("🔄 Calling handleDowngradeSubscription");
      const result = await handleDowngradeSubscription(currentPlan, newPlan);
      console.log("🔄 Downgrade result:", result);
      if (result.success) {
        console.log("✅ Downgrade scheduled successfully");
        setDowngradeSuccess(true);
        setDowngradeMessage(result.data?.message || `Your subscription will be downgraded to the ${newPlan} plan at the end of your billing period.`);
        setTimeout(() => {
          setIsRedirecting(false);
          setDowngradeSuccess(false);
          setDowngradeMessage(null);
          refetchSubscription();
        }, 3000);
      } else {
        console.error("❌ Failed to initiate downgrade:", result.error);
        toast.error("Failed to initiate downgrade");
        setIsRedirecting(false);
      }
      return result;
    } catch (error) {
      console.error("❌ Error initiating downgrade:", error);
      toast.error("Failed to initiate downgrade");
      setIsRedirecting(false);
      return { success: false, error };
    }
  };

  return {
    selectedPlan,
    isAnnual,
    setIsAnnual,
    isLoading,
    isPlansLoading,
    planConfigs,
    isPortalLoading,
    isDowngradeLoading,
    updateComplete,
    isRedirecting,
    downgradeSuccess,
    downgradeMessage,
    subscriptionStatus,
    handleConfirmPlan,
    handleOpenBillingPortal,
    handleDowngrade,
    setSelectedPlan: (plan: string | ((prevPlan: SubscriptionPlanType) => SubscriptionPlanType)) => {
      if (typeof plan === 'function') {
        setSelectedPlan(plan);
      } else {
        setSelectedPlan(plan as SubscriptionPlanType);
      }
    }
  };
};
